import { nextTick, Reactive, reactive } from "vue";
import { get, set } from "lodash-es";
import Block from "@/editor/block";
import IBlockEditor, { EventListener, EventType } from "@/editor/blockEditorInterface";
import { arrayMove } from "@/utilities/array-utils";
import BlockFactory from "@/editor/blockFactory";
import { SyncManager } from "@/utilities/syncManager";
import User from "@/editor/userInterface";
import { BlockUser, EditorOptions } from "@/editor/types";

const DEFAULT_EDITOR_OPTION: EditorOptions = {
  editable: false,
  idFieldName: "hashid",
  blockFieldName: "blocks",
};

export default abstract class BlockEditor implements IBlockEditor {
  static readonly MAX_BLOCK_COUNT: number = 99;

  private readonly _id: string;
  private readonly _options: EditorOptions;
  private readonly _blocks: Reactive<Block[]>;
  private readonly _error: Error;
  private readonly _editable: boolean;
  private readonly _lockedBlocks: Reactive<Map<string, User>>;
  private readonly eventListeners: Map<EventType, EventListener[]> = new Map();

  protected constructor(json: any = {}, opts?: EditorOptions) {
    opts = Object.assign({}, DEFAULT_EDITOR_OPTION, opts) as EditorOptions;

    this._id = get(json, opts.idFieldName ?? "hashid", get(json, "id", ""));
    this._blocks = reactive([]);
    this._options = opts;
    this._editable = opts.editable ?? false;
    this._selected = null;
    this._error = new Error();
    this._lockedBlocks = reactive(new Map<string, User>());

    this.appendRawAll(json).then();
  }

  protected _selected: BlockUser | null;

  get selected(): BlockUser | null {
    return this._selected;
  }

  get id(): string {
    return this._id;
  }

  get options(): EditorOptions {
    return this._options;
  }

  get editable(): boolean {
    return this._editable;
  }

  get error(): Error {
    return this._error;
  }

  get blocks(): Reactive<Block[]> {
    return this._blocks;
  }

  set blocks(blocks: Block[]) {
    this._blocks.splice(0, this._blocks.length, ...blocks);
  }

  get lockedBlocks(): Reactive<Map<string, User>> {
    return this._lockedBlocks;
  }

  getBlocks(): Reactive<Block[]> {
    return this.blocks;
  }

  on(event: EventType, listener: EventListener): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }

    this.eventListeners.get(event)!.push(listener);
  }

  off(event: EventType, listener: EventListener): void {
    const listeners = this.eventListeners.get(event);

    if (listeners) {
      const index = listeners.indexOf(listener);
      if (index !== -1) {
        listeners.splice(index, 1);
      }
    }
  }

  emit(event: EventType, value: any): void {
    const listeners = this.eventListeners.get(event);
    const allListeners = this.eventListeners.get("*");

    if (listeners) {
      listeners.forEach((listener) => listener(value, event));
    }

    if (allListeners) {
      allListeners.forEach((listener) => listener(value, event));
    }
  }

  isLockedBlock(blockHashId: string, user?: User): boolean {
    const lockedUser = this._lockedBlocks.get(blockHashId);

    if (lockedUser && user) {
      return user && lockedUser.hashid !== user.hashid;
    }

    return !!lockedUser;
  }

  addLockedBlock(blockHashId: string, user: User): void {
    this._lockedBlocks.set(blockHashId, user);
  }

  removeLockedBlock(blockHashId: string): void {
    this._lockedBlocks.delete(blockHashId);
  }

  clearLockedBlocks(): void {
    this._lockedBlocks.clear();
  }

  getLockedBlockUser(blockHashId: string): User | undefined {
    return this._lockedBlocks.get(blockHashId);
  }

  getOption(key: string, defaultValue: any = null): any {
    return get(this._options, key, defaultValue);
  }

  hasOption(key: string): boolean {
    return key in this._options;
  }

  isEmpty(): boolean {
    return this._blocks.length === 0;
  }

  isMax(): boolean {
    return this._blocks.length >= BlockEditor.MAX_BLOCK_COUNT;
  }

  isSelected(block: Block): boolean {
    return this._selected?.block.id === block?.id;
  }

  getSelected(): BlockUser | null {
    return this._selected;
  }

  sync() {
    return SyncManager.sync(this);
  }

  setProperty(property: keyof this, value: any) {
    set(this, property, value);
  }

  getProperty(property: keyof this): any {
    return get(this, property);
  }

  getIndex(block: Block): number {
    return this.blocks.findIndex((it) => it.id === block.id);
  }

  select(block: Block, user: User): void {
    if (this.isSelected(block)) {
      return;
    }

    this._selected = { block, user };
  }

  deselect(): void {
    if (this._selected) {
      this._selected = null;
    }
  }

  async appendAll(blocks: Block[]): Promise<Block[]> {
    const newBlocks = await Promise.all(blocks.map((block) => block.validate().then(() => block)));
    this._blocks.push(...newBlocks);

    return newBlocks;
  }

  async append(block: Block, prev?: Block): Promise<null | Block> {
    await block.validate();

    if (this._blocks.length > BlockEditor.MAX_BLOCK_COUNT) {
      alert("더 이상 필드를 추가할 수 없습니다.");
      return null;
    }

    block.setEditor(this);

    if (prev) {
      const index = this._blocks.findIndex((it) => it.id === prev.id);
      this._blocks.splice(index + 1, 0, block);
      this.emit("created", { block, position: index });
    } else {
      this._blocks.push(block);
      this.emit("created", { block, position: null });
    }

    return block;
  }

  async appendRawAll(json: any): Promise<Block[]> {
    const newBlocks = BlockFactory.getInstance().createMany(get(json, this._options.blockFieldName || "blocks", []), this, this.namespace());

    return this.appendAll(newBlocks);
  }

  async appendRaw(block: object, position?: number): Promise<null | Block> {
    const newBlock = BlockFactory.getInstance().create(block, this, this.namespace());
    const prevBlock = position ? this.findBlockByIndex(position) : undefined;

    return this.append(newBlock, prevBlock);
  }

  delete(block: Block): void {
    const index = this.findBlockIndex(block);

    if (index > -1) {
      this._blocks.splice(index, 1);
      this.emit("deleted", block);
    }

    nextTick(() => {
      this.deselect();
    });
  }

  deleteById(blockId: string): void {
    const block = this.findBlockById(blockId);

    if (block) {
      this.delete(block);
    }
  }

  insertBefore(block: Block, target: Block): void {
    const index = this.findBlockIndex(target);

    if (index > -1) {
      this._blocks.splice(index, 0, block);

      this.emit("created", { block, position: index });
    }
  }

  insertAfter(block: Block, target: Block): void {
    const index = this.findBlockIndex(target);

    if (index > -1) {
      this._blocks.splice(index + 1, 0, block);

      this.emit("created", { block, position: index });
    }
  }

  getPrevBlockOf(block: Block): Block | null {
    const index = this.findBlockIndex(block);

    if (index > 0) {
      return this.findBlockByIndex(index - 1);
    }

    return null;
  }

  getNextBlockOf(block: Block): Block | null {
    const index = this.findBlockIndex(block);

    if (index < this.blocks.length - 1) {
      return this.findBlockByIndex(index + 1);
    }

    return null;
  }

  has(block: Block): boolean {
    return this.blocks.some((it) => it.id === block.id);
  }

  update(newBlock: Block): void {
    const currentBlock = this.findBlockById(newBlock.id);

    if (currentBlock) {
      this.assignValue(currentBlock, newBlock);
    } else {
      this.createAndAddBlock(newBlock);
    }
  }

  updateRaw(value: any[]): void {
    value.forEach((newBlock) => {
      const block = this.findBlockById(newBlock.id);

      if (block) {
        this.update(newBlock);
      }
    });
  }

  moveById(blockId: string, position: number, user: User): void {
    const block = this.findBlockById(blockId);

    if (!block) {
      console.warn(`Block with id ${blockId} not found`);
      return;
    }

    this.move(block, position, user);
  }

  move(block: Block, position: number, user: User): void {
    const currentIndex = this.findBlockIndex(block);

    if (currentIndex === -1) {
      console.warn(`Block with id ${block.id} not found`);
      return;
    }

    if (position < 0 || position >= this.blocks.length) {
      console.warn(`Invalid new index: ${position}`);
      return;
    }

    arrayMove(this.blocks, currentIndex, position);
    this.emit("reordered", { block: block, from: currentIndex, to: position, user: user });
  }

  abstract namespace(): string;

  abstract toJSON(): any;

  protected findBlockById(id: string): any | undefined {
    return this.blocks.find((block) => block.id === id);
  }

  protected createAndAddBlock(newBlock: any): void {
    const createdBlock = this.appendRaw(newBlock);

    createdBlock.then((block) => {
      if (block) {
        this.blocks.push(block);
      }
    });
  }

  protected assignValue(target: any, source: any): void {
    Object.entries(source).forEach(([key, sourceValue]) => {
      const targetValue = target[key];
      if (this.isObject(sourceValue)) {
        this.assignObject(target, key, sourceValue);
      } else {
        this.assignPrimitive(target, key, targetValue, sourceValue);
      }
    });
  }

  protected assignObject(target: any, key: string, sourceValue: any): void {
    if (Array.isArray(sourceValue)) {
      this.assignArray(target, key, sourceValue);
    } else {
      target[key] = target[key] || {};
      this.assignValue(target[key], sourceValue);
    }
  }

  protected assignArray(target: any, key: string, sourceArray: any[]): void {
    target[key] = target[key] || [];
    sourceArray.forEach((item, index) => {
      if (this.isObject(item)) {
        target[key][index] = target[key][index] || {};
        this.assignValue(target[key][index], item);
      } else {
        target[key][index] = item;
      }
    });
  }

  protected assignPrimitive(target: any, key: string, targetValue: any, sourceValue: any): void {
    if (targetValue !== sourceValue) {
      key = this.toCamelCase(key);
      target[key] = sourceValue;
    }
  }

  protected isObject(value: any): boolean {
    return value && typeof value === "object" && !Array.isArray(value);
  }

  protected toCamelCase(value: string): string {
    return value.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
  }

  protected init(): any {
    return {};
  }

  private findBlockIndex(block: Block): number {
    return this.blocks.findIndex((it) => it.id === block.id);
  }

  private findBlockByIndex(index: number): Block {
    return <Block>this.blocks[index];
  }
}
